/** @jsxImportSource @emotion/react */
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { AppHomepageUpsertGeneralInfo } from "./AppHomepageUpsertGeneralInfo";
import moment from "moment";
import { AppHomepageNewsSection } from "./AppHomepageNewsSection";
import { AppHomepageOffersSection } from "./AppHomepageOffersSection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { toastr } from "react-redux-toastr";
import {
  CreateHomepage,
  HomepageForm,
  UpdateHomepage,
} from "../../model/HomepageConfigListSuccessResponse";
import {
  useCreateHomepage,
  useGetHomepage,
  useGetNewsList,
  useUpdateHomepage,
  useUploadHomepageImage,
} from "../../../../service/api";
import { UploadImage } from "../../../../service/api-response/UploadImage";
import { routes } from "../../../../appRoutingHelpers";
import { GoBackLinkButton } from "../../../../components/GoBackLinkButton";
import { AppHomepageImageSection } from "./AppHomepageImageSection";
import { StickyBottomBar } from "../../../../components/StickyBottomBar";
import { SpinnerBlock } from "../../../common/Spinner";
import { StyledForm } from "../../../../components/StyledForm";
import { PageWrapper } from "../../../../components/page-skeleton/PageWrapper";
import { stickyBottomBarBodyStyle } from "../generic-styles";
import { getCountrySpecificValues } from "../../../../util";
import { useSelector } from "react-redux";
import * as authSelectors from "../../../auth/authSelectors";
import {
  getErrorMessage,
  useHandleFetchError,
} from "../../../../error-handler";
import { ButtonAction } from "../../../../components/NewButtons";

const arraySchema = yup.object().shape({
  id: yup.string().required(),
  name: yup.string(),
});

const announcementSchema = yup.object().shape({
  announcementId: yup.string().required("Required"),
  name: yup.string(),
  validFrom: yup.string().required("Please enter valid from date"),
  validTo: yup.string(),
  sortKey: yup.number().required(),
});

const homepageSchema = yup.object().shape({
  name: yup.string().required("Please enter name to continue"),
  title: yup.string().required("Please enter title to continue"),
  validFrom: yup
    .string()
    .required("Please decide when the homepage is active from to continue"),
  validTo: yup.string(),
  files: yup.array().when("$isEdit", (isEdit, schema) => {
    if (!isEdit) {
      return schema.min(1, "Please add an image to continue");
    }
    return;
  }),
  imageUrl: yup.string().when("$isEdit", (isEdit, schema) => {
    if (isEdit) {
      return schema.required("Please add an image to continue");
    }
    return;
  }),
  announcementsItems: yup.array(announcementSchema),
  tableBookingUrl: yup.string(),
  videoUrl: yup.string().url("Please enter a valid URL"),
  vouchers: yup.array(arraySchema),
  countryCode: yup.string().required("Please choose country code to continue"),
});

export const AppHomepageUpsert = () => {
  const { id } = useParams<{
    id: string;
  }>();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const navigate = useNavigate();
  const isEdit = !!id;
  const { countryCode } = getCountrySpecificValues();

  const methods = useForm<HomepageForm>({
    resolver: yupResolver(homepageSchema),
    defaultValues: {
      files: [],
      countryCode: countryCode,
      announcementsItems: [],
    },
    context: {
      isEdit,
    },
  });

  const { reset, handleSubmit } = methods;
  const { data: homepage, error: getHomepageError } = useGetHomepage(
    organizationKeyName,
    id
  );

  const { data: newsList } = useGetNewsList(organizationKeyName);

  const {
    mutateAsync: createHomepage,
    isLoading: creationIsLoading,
  } = useCreateHomepage();

  const {
    mutateAsync: updateHomepage,
    isLoading: updateIsLoading,
  } = useUpdateHomepage();

  const {
    mutateAsync: uploadImage,
    isLoading: imageUploadisLoading,
  } = useUploadHomepageImage();

  const isLoading =
    creationIsLoading || updateIsLoading || imageUploadisLoading;

  const onSubmit = async (homepage: HomepageForm) => {
    const {
      validFrom,
      validTo,
      announcementsItems,
      vouchers,
      files,
      ...rest
    } = homepage;
    const announcementItems = (announcementsItems || [])
      .filter((x) => !!x && !!x.announcementId)
      .map((x) => ({
        announcementId: x.announcementId,
        validFrom: x.validFrom
          ? moment(x.validFrom).format()
          : moment().format(),
        validTo: x.validTo ? moment(x.validTo).format() : undefined,
        sortKey:
          typeof x.sortKey === "string"
            ? parseInt(x.sortKey, 10) || 0
            : x.sortKey || 0,
      }));

    const data: any = {
      ...rest,
      validFrom: validFrom ? moment(validFrom).format() : null,
      validTo: validTo ? moment(validTo).format() : null,
      announcementItems:
        announcementItems.length > 0 ? announcementItems : undefined,
      voucherIds: (vouchers || [])
        .filter((x: any) => !!x)
        .map((x: any) => x.id),
    };
    try {
      if (isEdit) {
        const request: UpdateHomepage = {
          organizationKeyName,
          id: id!,
          data,
        };
        await updateHomepage(request);
      } else {
        const request: CreateHomepage = {
          organizationKeyName,
          data,
        };
        const homepageId = await createHomepage(request);
        const file = files[0];
        const uploadImageRequest: UploadImage = {
          file,
          id: homepageId,
          organizationKeyName,
        };
        await uploadImage(uploadImageRequest);
      }
      toastr.success(
        "Success",
        `You succesfully ${isEdit ? "updated" : "created"} the homepage`
      );
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    } finally {
      navigate(routes.appHome(organizationKeyName));
    }
  };

  useEffect(() => {
    if (isEdit && homepage && newsList) {
      const { validFrom, validTo, announcementsItems, ...rest } = homepage;

      reset({
        validFrom: validFrom && moment(validFrom).format("YYYY-MM-DDTHH:mm"),
        validTo: validTo && moment(validTo).format("YYYY-MM-DDTHH:mm"),
        announcementsItems: (announcementsItems || [])
          .sort((a, b) => {
            const aSortKey =
              typeof a.sortKey === "number"
                ? a.sortKey
                : parseInt(a.sortKey?.toString() || "0", 10);
            const bSortKey =
              typeof b.sortKey === "number"
                ? b.sortKey
                : parseInt(b.sortKey?.toString() || "0", 10);
            return aSortKey - bSortKey;
          })
          .map((item, index) => ({
            ...item,
            sortKey: index + 1,
          })),
        ...rest,
      });
    }
  }, [homepage, newsList, reset, isEdit]);

  useHandleFetchError(getHomepageError);

  const buttonText = isEdit ? "Save changes" : "Create homepage";

  if (isEdit && !homepage) {
    return <SpinnerBlock />;
  }

  return (
    /* @ts-ignore */
    <FormProvider {...methods}>
      <StyledForm>
        <PageWrapper>
          <GoBackLinkButton
            to={routes.appHome(organizationKeyName)}
            text="Back to all homepages"
          />
          <AppHomepageUpsertGeneralInfo />
          <AppHomepageImageSection isEdit={isEdit} />
          <AppHomepageNewsSection />
          <AppHomepageOffersSection />
        </PageWrapper>
        <StickyBottomBar>
          <div css={stickyBottomBarBodyStyle}>
            <ButtonAction
              disabled={isLoading}
              type="button"
              onClick={handleSubmit(onSubmit)}
            >
              {isLoading ? (
                <FontAwesomeIcon icon={solid("spinner")} pulse spin />
              ) : (
                buttonText
              )}
            </ButtonAction>
          </div>
        </StickyBottomBar>
      </StyledForm>
    </FormProvider>
  );
};
